/**
 * δ.7 — voiceTurnSocket.ts: thin WS wrapper for `/api/voice/turn`.
 *
 * Per plan §4 δ.7 + DESIGN §5.2 + §10.4 + R1 C4 (`onClose` wired on
 * EVERY close path — no conditional gating).
 *
 * No reconnect logic (DESIGN §10.4 — user re-speaks on failure). Caller
 * (voiceStore.ts in δ.8) is responsible for teardown lifecycle.
 */

export interface VoiceTurnSocketCallbacks {
  url: string;
  /** Fires once when ws.onopen happens. Caller sends the mandatory
   * config frame here (DESIGN §4.1). */
  onOpen?: () => void;
  onText: (frame: Record<string, unknown>) => void;
  onBinary: (data: ArrayBuffer) => void;
  onError: (event: Event) => void;
  onClose: (code: number, reason: string) => void;
}

export class VoiceTurnSocket {
  private readonly ws: WebSocket;
  private readonly callbacks: VoiceTurnSocketCallbacks;
  private closed = false;

  constructor(callbacks: VoiceTurnSocketCallbacks) {
    this.callbacks = callbacks;
    this.ws = new WebSocket(callbacks.url);
    // R1 C4: bind onclose unconditionally. EVERY close path — normal 1000,
    // protocol-violation 1002, abnormal 1006, idle 4000 — must reach the
    // caller's `onClose` so `teardownTurn()` can release the AudioContext
    // (DESIGN §5.2 R2.3 — iOS Safari per-page cap defense).
    this.ws.onclose = (ev) => {
      this.closed = true;
      this.callbacks.onClose(ev.code, ev.reason);
    };
    this.ws.onerror = (ev) => {
      // R2 C8 — do NOT call teardown from here; the subsequent onclose
      // does that. Surface for UI status.
      this.callbacks.onError(ev);
    };
    this.ws.binaryType = 'arraybuffer';
    if (callbacks.onOpen) {
      this.ws.onopen = () => callbacks.onOpen!();
    }
    this.ws.onmessage = (ev) => {
      if (typeof ev.data === 'string') {
        try {
          const parsed = JSON.parse(ev.data) as Record<string, unknown>;
          this.callbacks.onText(parsed);
        } catch (err) {
          // Malformed JSON on text frame — log + drop. Rule #19: don't
          // silently fail; the frame is informational, not load-bearing.
          console.warn('[voiceTurnSocket] non-JSON text frame:', err);
        }
      } else if (ev.data instanceof ArrayBuffer) {
        this.callbacks.onBinary(ev.data);
      }
    };
  }

  send(payload: Record<string, unknown> | ArrayBuffer): void {
    if (this.closed || this.ws.readyState !== WebSocket.OPEN) return;
    if (payload instanceof ArrayBuffer) {
      this.ws.send(payload);
    } else {
      this.ws.send(JSON.stringify(payload));
    }
  }

  close(code = 1000): void {
    if (this.closed) return;
    try {
      this.ws.close(code);
    } catch {
      /* already closed */
    }
  }

  /**
   * Phase 9.7 (2026-05-24) — D-9.7-3 / D-9.7-6 cancel-turn client→server frame.
   * Sends `{type: 'cancel_turn', turn_id}` over the open WS. WS itself stays
   * open per D-9.7-4 (PWS persistent). Idempotent: backend listener also
   * ignores duplicates per turn_id, so double-sends from impatient users
   * are safe.
   */
  sendCancelTurn(turnId: string): void {
    this.send({ type: 'cancel_turn', turn_id: turnId });
  }

  get readyState(): number {
    return this.ws.readyState;
  }
}
